import apiClient, { ApiError, logResponseData } from './apiClient';
import { tryOrderProductPeriodOverlapFromApiError } from '@/lib/orderProductPeriodOverlapError';
import { extractYmdPrefix } from '../lib/dateYmd';
import type {
    ApiPaymentStatus,
    CreateOrderRequestDto,
    CreateOrderResponseDto,
    DeleteOrderResponseDto,
    GetOrderDetailResponseDto,
    GetOrderListRequestDto,
    GetOrderListResponseDto,
    OrderListByUserItemDto,
    OrderListItemDto,
    GetOrderOrganizationRequestDto,
    GetOrderStatisticsResponseDto,
    OrderItemDto,
    PurchaseOpenClassRequestDto,
    PurchaseOpenClassResponseDto,
    UpdateOrderRequestDto,
    UpdateOrderResponseDto,
} from '../types/api/order';

/** POST/PATCH 주문 본문 — CreateOrderRequestDto·UpdateOrderRequestDto: 라이선스 활성 시 startAt(이용 시작일) 필수 */
type OrderLicenseFields = { isLicenseActive?: boolean; startAt?: string | null };

/**
 * 라이선스 활성 시: startAt(이용 시작일)은 비어 있으면 안 되며, 로컬 기준 오늘을 넘길 수 없습니다.
 * (화면에서 빈 값은 오늘로 채운 뒤 호출하는 것이 규칙입니다.)
 */
export function validateOrderLicenseUsageStart(data: OrderLicenseFields): void {
    if (!data.isLicenseActive) return;
    const raw = typeof data.startAt === 'string' ? data.startAt.trim() : '';
    if (!raw) {
        throw new Error('라이선스를 활성으로 설정한 경우 사용 시작일이 필요합니다.');
    }
    const ymd = extractYmdPrefix(raw);
    if (!ymd || Number.isNaN(Date.parse(`${ymd}T12:00:00`))) {
        throw new Error('사용 시작일 형식이 올바르지 않습니다.');
    }
}

/** 목록 보강용 getDetail: 401/403은 `null`로 흡수(타입·래핑 차이로 `instanceof` 실패하는 경우 보완) */
function isListEnrichmentSkippableAuthzError(e: unknown): boolean {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) return true;
    if (e && typeof e === 'object' && 'status' in e) {
        const s = (e as { status?: number }).status;
        return s === 401 || s === 403;
    }
    return false;
}

/** GET /order/my → GET /order/list와 동일 UI용 DTO (학생 주문 목록) */
export function mapOrderListByUserItemToListItem(row: OrderListByUserItemDto): OrderListItemDto {
    // API 문서 기준: 타입을 신뢰 (optional을 null로 변환)
    const dealer = row.dealerOrganizationTitle;
    const orgFromRow = row.organizationName;
    const organizationName = orgFromRow ?? dealer;
    const userName = row.userName ?? null;
    const productName = row.productName ?? null;
    const productNames = row.productNames ?? null;
    const productTypes = row.productTypes ?? [];
    const ps = row.paymentStatus;
    const paymentStatus: ApiPaymentStatus =
        ps === 'UN_INVOICED' || ps === 'INVOICED' || ps === 'PAID' || ps === 'REFUNDED' ? ps : 'UN_INVOICED';
    return {
        orderId: row.id,
        orderNumber: row.orderNumber,
        orderType: row.orderType,
        userName,
        productTypes,
        regularPriceTotal: row.totalAmount,
        discountedPriceTotal: row.totalAmount,
        totalAmount: row.totalAmount,
        status: row.status,
        paymentStatus,
        isLicenseActive: row.isLicenseActive,
        createdAt: row.createdAt,
        organizationName,
        ...(productName ? { productName } : {}),
        ...(productNames && productNames.length > 0 ? { productNames } : {}),
        ...(row.startAt != null && String(row.startAt).trim() ? { startAt: row.startAt } : {}),
        ...(row.expiresAt != null && String(row.expiresAt).trim() ? { expiresAt: row.expiresAt } : {}),
        ...(row.usedAt != null && String(row.usedAt).trim() ? { usedAt: row.usedAt } : {}),
        ...(row.isOnlineClass != null ? { isOnlineClass: row.isOnlineClass } : {}),
    };
}

/** GET /order/{id}/detail 응답으로 PATCH 본문 조립 (입금상태 갱신 등) */
export function buildUpdateOrderDtoFromDetail(
    detail: GetOrderDetailResponseDto,
    overrides?: Partial<Pick<UpdateOrderRequestDto, 'paymentStatus' | 'description'>>
): UpdateOrderRequestDto {
    const items: OrderItemDto[] = detail.items.map((it) => ({
        productPriceId: it.productPriceId,
        quantity: it.quantity,
        unitPrice: it.unitPrice,
        ...(it.discountPrice != null && it.discountPrice > 0 ? { discountPrice: it.discountPrice } : {}),
    }));
    return {
        organizationId: detail.organizationId,
        downPayment: detail.downPayment,
        interimPayment: detail.interimPayment,
        finalPayment: detail.finalPayment,
        ...(detail.dealerOrganizationTitle?.trim() ? { dealerOrganizationTitle: detail.dealerOrganizationTitle.trim() } : {}),
        items,
        totalAmount: detail.totalAmount,
        isLicenseActive: detail.isLicenseActive,
        ...(detail.startAt ? { startAt: detail.startAt } : {}),
        ...(detail.description?.trim() ? { description: detail.description.trim() } : {}),
        ...overrides,
    };
}

/** GET /order/my 응답 raw 데이터를 OrderListByUserItemDto로 변환 — API 문서 기준 */
/** apiClient가 `getErrorMessage`로 이미 본문+검증 상세를 합친 문자열을 넣으므로 그대로 사용 */
function extractMessage(error: unknown): string {
    if (error instanceof ApiError) {
        return error.message;
    }
    if (error instanceof Error) return error.message;
    return '알 수 없는 오류가 발생했습니다.';
}

/** GET /order/list 쿼리 — organizationId는 스펙상 목록 필터로 사용 가능 */
function buildOrderListQuery(params: GetOrderListRequestDto): Record<string, string | number> {
    const query: Record<string, string | number> = {
        page: params.page,
        pageSize: params.pageSize,
    };
    if (params.searchText?.trim()) query.searchText = params.searchText.trim();
    if (params.organizationId != null && Number.isFinite(Number(params.organizationId))) {
        query.organizationId = Number(params.organizationId);
    }
    if (params.startDate?.trim()) query.startDate = params.startDate.trim();
    if (params.endDate?.trim()) query.endDate = params.endDate.trim();
    if (params.orderType) query.orderType = params.orderType;
    if (params.status) query.status = params.status;
    if (params.paymentStatus) query.paymentStatus = params.paymentStatus;
    /** GET /order/list 는 language 쿼리 미지원(백엔드 검증) — 다국어는 GET /order/my·GET /order/:id 등 i18n 엔드포인트 */
    return query;
}

export const orderService = {
    /** GET /order/list (관리자·기관 등 — 학생은 403일 수 있음) */
    getList: async (params: GetOrderListRequestDto): Promise<GetOrderListResponseDto> => {
        try {
            return await apiClient.get<GetOrderListResponseDto>('/order/list', { params: buildOrderListQuery(params) });
        } catch (error) {
            throw new Error(extractMessage(error) || '주문 목록을 불러오는데 실패했습니다.');
        }
    },

    /** GET /order/my — 로그인 사용자 본인 주문 (학생·게스트 등) */
    getMyList: async (params: { page: number; pageSize: number; language?: string }): Promise<GetOrderListResponseDto> => {
        try {
            const { language, ...rest } = params;
            const primary = await apiClient.get<GetOrderListResponseDto>(`/order/my`, {
                params: { ...rest, ...(language ? { language } : {}) },
            });
            if (primary.orders.length > 0) {
                return primary;
            }
            /** 스펙상 /order/my 는 CONFIRMED 만 줄 수 있어, 비어 있으면 본인 스코프 /order/list 폴백(403·401 무시) */
            try {
                const fb = await apiClient.get<GetOrderListResponseDto>('/order/list', {
                    params: { page: params.page, pageSize: params.pageSize },
                });
                if (fb.orders && fb.orders.length > 0) {
                    return fb;
                }
            } catch (e) {
                if (e instanceof ApiError && (e.status === 403 || e.status === 401)) {
                    return primary;
                }
                throw e;
            }
            return primary;
        } catch (error) {
            throw new Error(extractMessage(error) || '내 주문 목록을 불러오는데 실패했습니다.');
        }
    },

    /**
     * GET /order/organization — 기관 주문 목록. 기관 스코프는 **액세스 토큰**으로만 결정(쿼리에 organizationId 없음).
     * - 기본: 404 시 GET /order/list 로 폴백(organizationId 쿼리 없이 — 토큰 스코프와 동일 가정).
     * - `fallbackToListOn404: false`: 폴백 없음(예: Orders `embedMode`).
     *   (마스터 포털 `/master/orders` 개인 내역은 `getMyList` / GET `/order/my`.)
     */
    getOrganizationList: async (
        params: GetOrderOrganizationRequestDto,
        options?: { fallbackToListOn404?: boolean }
    ): Promise<GetOrderListResponseDto> => {
        const orgQuery: Record<string, string | number> = {
            page: params.page,
            pageSize: params.pageSize,
        };
        if (params.searchText?.trim()) orgQuery.searchText = params.searchText.trim();
        if (params.startDate?.trim()) orgQuery.startDate = params.startDate.trim();
        if (params.endDate?.trim()) orgQuery.endDate = params.endDate.trim();
        if (params.orderType) orgQuery.orderType = params.orderType;
        if (params.status) orgQuery.status = params.status;
        if (params.paymentStatus) orgQuery.paymentStatus = params.paymentStatus;

        const allowListFallback = options?.fallbackToListOn404 !== false;

        try {
            return await apiClient.get<GetOrderListResponseDto>('/order/organization', { params: orgQuery });
        } catch (error) {
            const notFound = error instanceof ApiError && error.status === 404;
            if (!notFound) {
                throw new Error(extractMessage(error) || '기관 주문 내역을 불러오는데 실패했습니다.');
            }
            if (!allowListFallback) {
                throw new Error(extractMessage(error) || '기관 주문 내역을 불러오는데 실패했습니다.');
            }
            try {
                return await apiClient.get<GetOrderListResponseDto>('/order/list', {
                    params: buildOrderListQuery({
                        page: params.page,
                        pageSize: params.pageSize,
                        searchText: params.searchText,
                        startDate: params.startDate,
                        endDate: params.endDate,
                        orderType: params.orderType,
                        status: params.status,
                        paymentStatus: params.paymentStatus,
                    }),
                });
            } catch (fallbackErr) {
                throw new Error(extractMessage(fallbackErr) || '기관 주문 내역을 불러오는데 실패했습니다.');
            }
        }
    },

    /**
     * 주문 상세 — 백엔드마다 `GET /order/{id}/detail` 만 있거나 `GET /order/{id}` 만 있는 경우가 있어 둘 다 시도.
     * (문서·OpenAPI: GET /order/{orderId}/detail)
     */
    getDetail: async (orderId: number, language?: string): Promise<GetOrderDetailResponseDto> => {
        const params = language ? { language } : {};
        const opts = { params };
        try {
            const result = await apiClient.get<GetOrderDetailResponseDto>(`/order/${orderId}/detail`, opts);
            logResponseData(result);
            return result;
        } catch (e) {
            if (e instanceof ApiError && e.status === 404) {
                try {
                    const result = await apiClient.get<GetOrderDetailResponseDto>(`/order/${orderId}`, opts);
                    logResponseData(result);
                    return result;
                } catch (e2) {
                    if (e2 instanceof ApiError) throw e2;
                    throw new Error(extractMessage(e2) || '주문 정보를 불러오는데 실패했습니다.');
                }
            }
            if (e instanceof ApiError) throw e;
            throw new Error(extractMessage(e) || '주문 정보를 불러오는데 실패했습니다.');
        }
    },

    /**
     * 주문 **목록 행 보강** 전용(OrgPage `OrderManagement`, `Orders` 등 `useQueries`).
     * 마스터·기관 맥락에서 `GET /order/{id}/detail` 이 401/403 일 수 있음(역할/스코프) — 콘솔 에러·Query 실패로 이어지지 않게 `null` 반환.
     * 화면 단독 `주문 상세` 페이지는 `getDetail` 그대로(실패 시 에러 표시).
     */
    getDetailForListEnrichment: async function (
        this: { getDetail: (orderId: number, language?: string) => Promise<GetOrderDetailResponseDto> },
        orderId: number,
        language?: string
    ): Promise<GetOrderDetailResponseDto | null> {
        try {
            return await this.getDetail(orderId, language);
        } catch (e) {
            if (isListEnrichmentSkippableAuthzError(e)) {
                return null;
            }
            throw e;
        }
    },

    /** POST /order */
    create: async (data: CreateOrderRequestDto): Promise<CreateOrderResponseDto> => {
        try {
            validateOrderLicenseUsageStart(data);
            // POST /order는 totalCostAmount·paymentStatus·품목 costPrice 미수신 시 검증 오류 방지 → 허용 필드만 전송
            const payload: CreateOrderRequestDto = {
                organizationId: data.organizationId,
                downPayment: data.downPayment,
                interimPayment: data.interimPayment,
                finalPayment: data.finalPayment,
                ...(data.dealerOrganizationTitle ? { dealerOrganizationTitle: data.dealerOrganizationTitle } : {}),
                items: data.items.map(({ productPriceId, quantity, unitPrice, discountPrice }) => ({
                    productPriceId,
                    quantity,
                    unitPrice,
                    ...(discountPrice != null && discountPrice > 0 ? { discountPrice } : {}),
                })),
                totalAmount: data.totalAmount,
                ...(data.description ? { description: data.description } : {}),
                isLicenseActive: data.isLicenseActive,
                ...(data.startAt ? { startAt: data.startAt } : {}),
            };
            return await apiClient.post<CreateOrderResponseDto>('/order', payload);
        } catch (error) {
            const overlap = tryOrderProductPeriodOverlapFromApiError(error);
            if (overlap) throw overlap;
            if (error instanceof ApiError && error.status === 509) {
                throw new Error(extractMessage(error) || '동일 프로덕트 사용기간이 겹칩니다.');
            }
            throw new Error(extractMessage(error) || '주문 등록에 실패했습니다.');
        }
    },

    /** POST /order/open-class — OpenAPI OrderController_purchaseOpenClass */
    purchaseOpenClass: async (data: PurchaseOpenClassRequestDto): Promise<PurchaseOpenClassResponseDto> => {
        try {
            return await apiClient.post<PurchaseOpenClassResponseDto>(`/order/open-class`, {
                classIds: data.classIds.map((id) => Math.floor(Number(id))).filter((id) => Number.isFinite(id) && id > 0),
                ...(data.language ? { language: data.language } : {}),
            });
        } catch (error) {
            const overlap = tryOrderProductPeriodOverlapFromApiError(error);
            if (overlap) throw overlap;
            throw new Error(extractMessage(error) || '오픈클래스 주문에 실패했습니다.');
        }
    },

    /** PATCH /order/{orderId} */
    update: async (orderId: number, data: UpdateOrderRequestDto): Promise<UpdateOrderResponseDto> => {
        try {
            /** items는 POST와 동일 형태. 총 원가는 백엔드가 PATCH에서 totalCostAmount 미수신 → description 임베드만 사용 */
            const payload: Record<string, unknown> = {
                organizationId: data.organizationId,
                downPayment: data.downPayment,
                interimPayment: data.interimPayment,
                finalPayment: data.finalPayment,
                items: data.items.map(({ productPriceId, quantity, unitPrice, discountPrice }) => ({
                    productPriceId,
                    quantity,
                    unitPrice,
                    ...(discountPrice != null && discountPrice > 0 ? { discountPrice } : {}),
                })),
                totalAmount: data.totalAmount,
                isLicenseActive: data.isLicenseActive,
            };
            if (data.status) payload.status = data.status;
            if (data.dealerOrganizationTitle != null && data.dealerOrganizationTitle.trim())
                payload.dealerOrganizationTitle = data.dealerOrganizationTitle.trim();
            if (data.description != null && data.description.trim())
                payload.description = data.description.trim();
            if (data.startAt != null && data.startAt.trim())
                payload.startAt = data.startAt.trim();
            if (data.paymentStatus)
                payload.paymentStatus = data.paymentStatus;

            return await apiClient.patch<UpdateOrderResponseDto>(`/order/${orderId}`, payload);
        } catch (error) {
            const overlap = tryOrderProductPeriodOverlapFromApiError(error);
            if (overlap) throw overlap;
            if (error instanceof ApiError) {
                if (error.status === 404) {
                    throw new Error('클래스에서 사용중인 판매단위는 삭제할 수 없습니다. 클래스를 삭제 후 다시 시도해주세요');
                }
                if (error.status === 509) {
                    throw new Error(extractMessage(error) || '동일 프로덕트 사용기간이 겹칩니다.');
                }
            }
            throw new Error(extractMessage(error) || '주문 수정에 실패했습니다.');
        }
    },

    /** DELETE /order/{orderId} */
    delete: async (orderId: number): Promise<DeleteOrderResponseDto> => {
        try {
            return await apiClient.delete<DeleteOrderResponseDto>(`/order/${orderId}`);
        } catch (error) {
            if (error instanceof ApiError) {
                if (error.status === 404) {
                    throw new Error('클래스에서 사용중인 판매단위는 삭제할 수 없습니다. 클래스를 삭제 후 다시 시도해주세요');
                }
                if (error.status === 400) {
                    const msg = extractMessage(error);
                    if (msg.includes('라이선스') || msg.includes('license')) {
                        throw new Error('라이선스가 활성화된 주문은 삭제할 수 없습니다.');
                    }
                    if (msg.includes('클래스') || msg.includes('사용중') || msg.includes('class')) {
                        throw new Error('다른 클래스에서 사용중인 주문은 삭제할 수 없습니다.');
                    }
                    throw new Error(msg || '주문을 삭제할 수 없습니다.');
                }
            }
            throw new Error(extractMessage(error) || '주문 삭제에 실패했습니다.');
        }
    },

    /** GET /order/statistics?startDate=&endDate= */
    getStatistics: async (startDate: string, endDate: string): Promise<GetOrderStatisticsResponseDto> => {
        try {
            return await apiClient.get<GetOrderStatisticsResponseDto>('/order/statistics', {
                params: { startDate, endDate },
            });
        } catch (error) {
            throw new Error(extractMessage(error) || '주문 통계를 불러오는데 실패했습니다.');
        }
    },
};
